import { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api/axios';

const TABS = [
  { key: 'state', label: 'State', icon: '🗺️' },
  { key: 'district', label: 'District', icon: '🧭' },
  { key: 'city', label: 'City', icon: '📍' },
  { key: 'area', label: 'Area', icon: '🏘️' },
];

const COLORS = ['#7C3AED', '#22C55E', '#F59E0B', '#EF4444', '#1D5FA8', '#8B5A0B', '#0F766E', '#9CA3AF', '#FB7C32', '#6B6B6B'];

const PARENT = {
  state: null,
  district: { type: 'state', label: 'Belongs to state', placeholder: '— Select State —' },
  city: { type: 'district', label: 'Belongs to district', placeholder: '— Select District —' },
  area: { type: 'city', label: 'Belongs to city', placeholder: '— Select City —' },
};

const EXTRA = {
  state: { label: 'Code (optional)', head: 'Code', placeholder: 'e.g. KA' },
  area: { label: 'Pincode (optional)', head: 'Pincode', placeholder: 'e.g. 560038' },
};

const SAMPLE = {
  state: 'name,code,color,active\nKarnataka,KA,#7C3AED,1\nTamil Nadu,TN,#22C55E,1\n',
  district: 'name,state,color,active\nBengaluru Urban,Karnataka,#7C3AED,1\nKrishnagiri,Tamil Nadu,#22C55E,1\n',
  city: 'name,district,state,color,active\nBengaluru,Bengaluru Urban,Karnataka,#7C3AED,1\nHosur,Krishnagiri,Tamil Nadu,#22C55E,1\n',
  area: 'name,city,district,pincode,color,active\nIndiranagar,Bengaluru,Bengaluru Urban,560038,#F59E0B,1\n',
};

const EMPTY = { name: '', parent_id: '', state_id: '', color: COLORS[0], extra: '' };
const PAGE = 50;

const fmt = (d) =>
  d
    ? new Date(d)
        .toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
        .replace(',', '')
    : '—';

const errText = (err, fallback) =>
  err.response?.data?.errors
    ? Object.values(err.response.data.errors).flat()[0]
    : err.response?.data?.message || fallback;

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

function LocationMasterPanel() {
  const [type, setType] = useState('state');
  const [rows, setRows] = useState([]);
  const [parents, setParents] = useState([]);
  const [states, setStates] = useState([]); // only used on the City tab (State -> District picker)
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const fileRef = useRef(null);

  const parentCfg = PARENT[type];
  const extraCfg = EXTRA[type];
  const base = `/admin/location-masters/${type}`;
  const tab = TABS.find((t) => t.key === type);
  const none = Promise.resolve({ data: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [list, par, st] = await Promise.all([
        api.get(base),
        parentCfg ? api.get(`/admin/location-masters/${parentCfg.type}`) : none,
        type === 'city' ? api.get('/admin/location-masters/state') : none,
      ]);
      setRows(list.data);
      setParents(par.data);
      setStates(st.data);
    } catch (err) {
      setMsg({ ok: '', err: errText(err, 'Could not load the list.') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setForm(EMPTY);
    setEditingId(null);
    setSearch('');
    setLimit(PAGE);
    setMsg({ ok: '', err: '' });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.name.toLowerCase().includes(q) || (r.parent_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  // City tab: districts shown depend on the chosen state
  const parentOptions = useMemo(() => {
    if (type === 'city') return parents.filter((p) => String(p.parent_id) === String(form.state_id));
    return parents;
  }, [parents, type, form.state_id]);

  const parentLabel = (p) => (type === 'area' && p.parent_name ? `${p.name} (${p.parent_name})` : p.name);

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name,
      parent_id: r.parent_id ?? '',
      state_id: r.state_id ?? '',
      color: r.color || COLORS[0],
      extra: r.extra ?? '',
    });
    setMsg({ ok: '', err: '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg({ ok: '', err: '' });
    try {
      const payload = { name: form.name, color: form.color };
      if (parentCfg) payload.parent_id = form.parent_id;
      if (type === 'state') payload.code = form.extra;
      if (type === 'area') payload.pincode = form.extra;
      if (editingId) {
        await api.put(`${base}/${editingId}`, payload);
      } else {
        await api.post(base, payload);
      }
      setMsg({ ok: editingId ? `${tab.label} updated.` : `${tab.label} added.`, err: '' });
      setEditingId(null);
      setForm({ ...EMPTY, state_id: type === 'city' ? form.state_id : '' });
      await load();
    } catch (err) {
      setMsg({ ok: '', err: errText(err, 'Could not save.') });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r) => {
    const next = r.status === 'active' ? 'inactive' : 'active';
    const prev = rows;
    setRows((list) => list.map((x) => (x.id === r.id ? { ...x, status: next } : x)));
    try {
      await api.put(`${base}/${r.id}`, {
        name: r.name,
        color: r.color || undefined,
        status: next,
        ...(parentCfg ? { parent_id: r.parent_id } : {}),
      });
    } catch (err) {
      setRows(prev);
      setMsg({ ok: '', err: errText(err, 'Could not change status.') });
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try {
      await api.delete(`${base}/${r.id}`);
      setRows((list) => list.filter((x) => x.id !== r.id));
      if (editingId === r.id) cancelEdit();
    } catch (err) {
      setMsg({ ok: '', err: errText(err, 'Could not delete.') });
    }
  };

  const exportCsv = async () => {
    setBusy(true);
    try {
      const res = await api.get(`${base}/export`, { responseType: 'blob' });
      downloadBlob(res.data, `${type}s.csv`);
    } catch (err) {
      setMsg({ ok: '', err: 'Could not export the CSV.' });
    } finally {
      setBusy(false);
    }
  };

  const sampleCsv = () => downloadBlob(new Blob([SAMPLE[type]], { type: 'text/csv' }), `${type}-sample.csv`);

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setMsg({ ok: '', err: '' });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`${base}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg({ ok: res.data.message, err: '' });
      await load();
    } catch (err) {
      setMsg({ ok: '', err: errText(err, 'Import failed.') });
    } finally {
      setBusy(false);
    }
  };

  const undoImport = async () => {
    if (!window.confirm('Remove everything added by the last CSV import?')) return;
    setBusy(true);
    setMsg({ ok: '', err: '' });
    try {
      const res = await api.post(`${base}/undo-import`);
      setMsg({ ok: res.data.message, err: '' });
      await load();
    } catch (err) {
      setMsg({ ok: '', err: errText(err, 'Could not undo the import.') });
    } finally {
      setBusy(false);
    }
  };

  const toolBtn = 'text-xs font-semibold px-3.5 py-2 rounded-lg border transition disabled:opacity-50';
  const colSpan = 6 + (parentCfg ? 1 : 0) + (extraCfg ? 1 : 0);
  const fieldCls = 'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
  const labelCls = 'block text-xs font-semibold text-gray-500 mb-1.5';

  return (
    <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-6">
      <h3 className="font-bold text-[#0D1B3D] text-lg mb-1">Locations</h3>
      <p className="text-sm text-gray-500 mb-4">State → District → City → Area. Each level belongs to the one before it.</p>

      <div className="flex gap-2 flex-wrap mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setType(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
              type === t.key
                ? 'bg-[#7C3AED] text-white border-[#7C3AED]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'
            }`}
          >
            <span className="mr-1.5">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {msg.err && <p className="mb-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{msg.err}</p>}
      {msg.ok && <p className="mb-3 text-sm text-green-700 bg-green-50 rounded-lg px-3 py-2">{msg.ok}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* List */}
        <div className="lg:col-span-2 border border-gray-100 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h4 className="font-semibold text-[#0D1B3D]">{tab.label} List</h4>
            <span className="text-xs text-gray-400">{rows.length} total</span>
          </div>

          <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap gap-2">
            <button type="button" onClick={exportCsv} disabled={busy} className={`${toolBtn} bg-purple-50 text-[#7C3AED] border-purple-200 hover:bg-purple-100`}>
              ⬇ Export CSV
            </button>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className={`${toolBtn} bg-green-50 text-green-700 border-green-200 hover:bg-green-100`}>
              ⬆ Import CSV
            </button>
            <button type="button" onClick={sampleCsv} className={`${toolBtn} bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100`}>
              📄 Sample CSV
            </button>
            <button type="button" onClick={undoImport} disabled={busy} className={`${toolBtn} bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100`}>
              ↩ Undo Last Import
            </button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" />
          </div>

          <div className="px-5 py-3 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setLimit(PAGE); }}
              placeholder={`Search ${type}...`}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-400 text-[11px] tracking-wider uppercase">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">#</th>
                  <th className="text-left px-3 py-3 font-semibold">Name</th>
                  {parentCfg && <th className="text-left px-3 py-3 font-semibold">Belongs to {parentCfg.type}</th>}
                  {extraCfg && <th className="text-left px-3 py-3 font-semibold">{extraCfg.head}</th>}
                  <th className="text-left px-3 py-3 font-semibold">Color</th>
                  <th className="text-left px-3 py-3 font-semibold">Active</th>
                  <th className="text-left px-3 py-3 font-semibold">Action</th>
                  <th className="text-left px-3 py-3 font-semibold">Last updated</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={colSpan} className="px-5 py-8 text-center text-gray-400">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="px-5 py-8 text-center text-gray-400">
                      {rows.length === 0 ? `No ${type}s yet. Add one on the right or import a CSV.` : 'Nothing matches your search.'}
                    </td>
                  </tr>
                ) : (
                  filtered.slice(0, limit).map((r) => (
                    <tr key={r.id} className={`border-t border-gray-50 hover:bg-purple-50/40 ${editingId === r.id ? 'bg-purple-50/60' : ''}`}>
                      <td className="px-5 py-3 text-xs text-gray-400">{r.id}</td>
                      <td className="px-3 py-3 font-medium text-gray-800">{r.name}</td>
                      {parentCfg && <td className="px-3 py-3 text-gray-500">{r.parent_name || '—'}</td>}
                      {extraCfg && <td className="px-3 py-3 text-gray-500">{r.extra || '—'}</td>}
                      <td className="px-3 py-3">
                        <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: r.color || '#9CA3AF' }} />
                      </td>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={r.status === 'active'}
                          aria-label={`${r.name} active`}
                          onClick={() => toggleActive(r)}
                          className={`relative h-6 w-11 rounded-full transition-colors ${r.status === 'active' ? 'bg-[#7C3AED]' : 'bg-gray-300'}`}
                        >
                          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${r.status === 'active' ? 'left-[22px]' : 'left-0.5'}`} />
                        </button>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1.5">
                          <button type="button" onClick={() => startEdit(r)} title="Edit" className="h-8 w-8 rounded-lg border border-gray-200 hover:bg-purple-50">✏️</button>
                          <button type="button" onClick={() => remove(r)} title="Delete" className="h-8 w-8 rounded-lg border border-gray-200 hover:bg-red-50">🗑</button>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(r.updated_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filtered.length > limit && (
            <div className="px-5 py-3 border-t border-gray-100 text-center">
              <button type="button" onClick={() => setLimit((l) => l + PAGE)} className="text-sm font-semibold text-[#7C3AED] hover:underline">
                Show more ({filtered.length - limit} left)
              </button>
            </div>
          )}
        </div>

        {/* Add / edit form */}
        <form onSubmit={submit} className="border border-gray-100 rounded-2xl p-5">
          <h4 className="font-semibold text-[#0D1B3D] pb-3 mb-4 border-b border-gray-100">
            {editingId ? `Edit ${tab.label}` : `Add ${tab.label}`}
          </h4>

          <label className={labelCls}>{tab.label.toUpperCase()} NAME *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder={`Enter ${type} name`}
            required
            className={fieldCls}
          />

          {type === 'city' && (
            <>
              <label className={labelCls}>BELONGS TO STATE *</label>
              <select
                value={form.state_id}
                onChange={(e) => setForm((f) => ({ ...f, state_id: e.target.value, parent_id: '' }))}
                required
                className={fieldCls + ' bg-white'}
              >
                <option value="">— Select State —</option>
                {states.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </>
          )}

          {parentCfg && (
            <>
              <label className={labelCls}>{parentCfg.label.toUpperCase()} *</label>
              <select
                value={form.parent_id}
                onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
                required
                disabled={type === 'city' && !form.state_id}
                className={fieldCls + ' bg-white disabled:bg-gray-50 disabled:text-gray-400'}
              >
                <option value="">{parentCfg.placeholder}</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>{parentLabel(p)}</option>
                ))}
              </select>
            </>
          )}

          {extraCfg && (
            <>
              <label className={labelCls}>{extraCfg.label.toUpperCase()}</label>
              <input
                type="text"
                maxLength={10}
                value={form.extra}
                onChange={(e) => setForm((f) => ({ ...f, extra: e.target.value }))}
                placeholder={extraCfg.placeholder}
                className={fieldCls}
              />
            </>
          )}

          <label className={labelCls}>COLOR</label>
          <div className="flex flex-wrap gap-2 mb-5">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Color ${c}`}
                onClick={() => setForm((f) => ({ ...f, color: c }))}
                className={`h-6 w-6 rounded-full transition ${form.color === c ? 'ring-2 ring-offset-2 ring-[#7C3AED]' : ''}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={saving || !form.name.trim() || (parentCfg && !form.parent_id)}
            className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : editingId ? 'Save changes' : `+ Add ${tab.label}`}
          </button>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="w-full mt-2 text-sm font-medium text-gray-500 hover:text-gray-700 py-2">
              Cancel
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

export default LocationMasterPanel;